/**
 * useExams Hook (THPT App)
 * Manages THPT exam data fetching from backend API
 */

import { useState, useEffect, useCallback } from 'react';
import { apiService, type ExamQueryParams } from '@vsat/shared/lib/api';
import type { ExamInfo } from '../types/examTypes';

interface UseExamsOptions {
  autoFetch?: boolean;
  initialParams?: ExamQueryParams;
}

interface UseExamsReturn {
  exams: ExamInfo[];
  readyExams: ExamInfo[];
  loading: boolean;
  error: string | null;
  total: number;
  fetchExams: (params?: ExamQueryParams) => Promise<void>;
  fetchReadyExams: (params?: ExamQueryParams) => Promise<void>;
  refreshExams: () => Promise<void>;
  getExamById: (id: string) => Promise<ExamInfo | null>;
}

export function useExams(options: UseExamsOptions = {}): UseExamsReturn {
  const { autoFetch = true, initialParams } = options;

  const [exams, setExams] = useState<ExamInfo[]>([]);
  const [readyExams, setReadyExams] = useState<ExamInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchExams = useCallback(async (params?: ExamQueryParams) => {
    try {
      setLoading(true);
      setError(null);

      // Don't filter by standard - filter by systemContext instead
      // The backend will automatically filter by systemContext based on X-System-Context header (THPT)
      // This shows exams where systemContext = 'THPT' or 'BOTH', regardless of standard
      // standard is about exam format (VSAT 150 points vs THPT 10 points)
      // systemContext is about which app/platform the exam should appear in
      // Remove 'standard' from params if it's 'all' (backend doesn't accept 'all')
      const cleanParams = params ? { ...params } : undefined;
      if (cleanParams && (cleanParams as any).standard === 'all') {
        delete (cleanParams as any).standard;
      }
      const response = await apiService.getExams(cleanParams);
      const transformedExams = response.exams.map((exam: any) =>
        apiService.transformExamData(exam)
      );

      setExams(transformedExams);
      setTotal(response.total);
    } catch (err) {
      console.error('Failed to fetch THPT exams:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch exams');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReadyExams = useCallback(async (params?: ExamQueryParams) => {
    try {
      setLoading(true);
      setError(null);

      // Don't filter by standard - filter by systemContext instead
      // The backend will automatically filter by systemContext based on X-System-Context header (THPT)
      // This shows exams where systemContext = 'THPT' or 'BOTH', regardless of standard
      // standard is about exam format (VSAT 150 points vs THPT 10 points)
      // systemContext is about which app/platform the exam should appear in
      // Remove 'standard' from params if it's 'all' (backend doesn't accept 'all')
      const cleanParams = params ? { ...params } : undefined;
      if (cleanParams && (cleanParams as any).standard === 'all') {
        delete (cleanParams as any).standard;
      }
      const response = await apiService.getReadyExams(cleanParams);
      const transformedExams = response.exams.map((exam: any) =>
        apiService.transformExamData(exam)
      );

      setReadyExams(transformedExams);
      setTotal(response.total);
    } catch (err) {
      console.error('Failed to fetch ready THPT exams:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch ready exams');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshExams = useCallback(
    async (params = initialParams) => {
      await Promise.all([fetchExams(params), fetchReadyExams(params)]);
    },
    [fetchExams, fetchReadyExams, initialParams]
  );

  const getExamById = useCallback(async (id: string): Promise<ExamInfo | null> => {
    try {
      const exam = await apiService.getExamForStudent(id);
      return apiService.transformExamData(exam);
    } catch (err) {
      console.error('Failed to fetch exam by ID:', err);
      return null;
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      refreshExams(initialParams);
    }
  }, [autoFetch]);

  return {
    exams,
    readyExams,
    loading,
    error,
    total,
    fetchExams,
    fetchReadyExams,
    refreshExams,
    getExamById,
  };
}
